// Apolo IDE — HunkDiff (Sprint v11 E3).
//
// Renders a list of hunks from `computeHunks(oldText, newText)` with a
// per-hunk accept/reject toggle + bulk Accept all / Reject all controls.
// Calling `onApply(newText)` lifts the assembled new content (using only
// the accepted hunks) back to the parent so it can persist via Electron
// file IPC, git apply, or any other mechanism.

import { useCallback, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, X } from 'lucide-react';
import { applyHunks, computeHunks } from '../../lib/diffHunks.js';

/**
 * @param {{
 *   filePath?: string,
 *   oldText: string,
 *   newText: string,
 *   onApply?: (newText: string, acceptedIds: string[]) => void,
 *   onReject?: () => void,
 * }} props
 */
export default function HunkDiff({ filePath, oldText, newText, onApply, onReject }) {
  const hunks = useMemo(() => computeHunks(oldText, newText), [oldText, newText]);
  const [acceptedIds, setAcceptedIds] = useState(() => new Set(hunks.map((h) => h.id)));
  const [collapsed, setCollapsed] = useState(() => new Set());

  const allAccepted = hunks.length > 0 && hunks.every((h) => acceptedIds.has(h.id));
  const noneAccepted = hunks.length > 0 && !hunks.some((h) => acceptedIds.has(h.id));

  const toggleHunk = useCallback((id) => {
    setAcceptedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleCollapse = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const acceptAll = useCallback(() => setAcceptedIds(new Set(hunks.map((h) => h.id))), [hunks]);
  const rejectAll = useCallback(() => setAcceptedIds(new Set()), []);

  const handleApply = useCallback(() => {
    const result = applyHunks(oldText, hunks, acceptedIds);
    onApply?.(result, Array.from(acceptedIds));
  }, [oldText, hunks, acceptedIds, onApply]);

  if (hunks.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-4 py-6 text-center text-xs text-apolo-textTertiary">
        Nenhuma diferença entre old e new.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between gap-2 border-b border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2">
        <div className="min-w-0 truncate text-xs">
          <span className="text-apolo-textSecondary">{filePath || 'diff'}</span>
          <span className="ml-2 text-apolo-textTertiary">
            {hunks.length} hunk{hunks.length === 1 ? '' : 's'} ·{' '}
            {acceptedIds.size} aceito{acceptedIds.size === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={acceptAll}
            disabled={allAccepted}
            className="rounded-md border border-apolo-success/40 px-2 py-1 text-[11px] text-apolo-success transition hover:bg-apolo-successBg disabled:cursor-not-allowed disabled:opacity-50"
          >
            Aceitar tudo
          </button>
          <button
            type="button"
            onClick={rejectAll}
            disabled={noneAccepted}
            className="rounded-md border border-apolo-borderSubtle px-2 py-1 text-[11px] text-apolo-textSecondary transition hover:border-apolo-error/40 hover:text-apolo-error disabled:cursor-not-allowed disabled:opacity-50"
          >
            Rejeitar tudo
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto bg-apolo-bg">
        {hunks.map((h) => {
          const accepted = acceptedIds.has(h.id);
          const isCollapsed = collapsed.has(h.id);
          return (
            <article key={h.id} className="border-b border-apolo-borderSubtle">
              <header className="flex items-center justify-between gap-2 bg-apolo-surface1 px-3 py-1.5 text-[11px]">
                <button
                  type="button"
                  onClick={() => toggleCollapse(h.id)}
                  className="flex min-w-0 items-center gap-1 text-apolo-textTertiary transition hover:text-apolo-textPrimary"
                  aria-expanded={!isCollapsed}
                  aria-controls={`hunk-body-${h.id}`}
                >
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                  <code className="truncate font-mono">{h.header}</code>
                </button>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => !accepted && toggleHunk(h.id)}
                    aria-pressed={accepted}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition ${
                      accepted
                        ? 'border-apolo-success/50 bg-apolo-successBg text-apolo-success'
                        : 'border-apolo-borderSubtle text-apolo-textTertiary hover:border-apolo-success/40 hover:text-apolo-success'
                    }`}
                  >
                    <Check size={10} aria-hidden="true" />
                    Aplicar
                  </button>
                  <button
                    type="button"
                    onClick={() => accepted && toggleHunk(h.id)}
                    aria-pressed={!accepted}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition ${
                      !accepted
                        ? 'border-apolo-error/50 bg-apolo-errorBg text-apolo-error'
                        : 'border-apolo-borderSubtle text-apolo-textTertiary hover:border-apolo-error/40 hover:text-apolo-error'
                    }`}
                  >
                    <X size={10} aria-hidden="true" />
                    Pular
                  </button>
                </div>
              </header>
              {!isCollapsed && (
                <div id={`hunk-body-${h.id}`} className="font-mono text-[11px]">
                  {h.lines.map((l, idx) => {
                    const prefix = l.type === 'add' ? '+' : l.type === 'del' ? '-' : ' ';
                    const bg = l.type === 'add'
                      ? 'bg-apolo-success/10 text-apolo-success'
                      : l.type === 'del'
                        ? 'bg-apolo-error/10 text-apolo-error'
                        : 'text-apolo-textSecondary';
                    return (
                      <div
                        key={`${h.id}-${idx}`}
                        className={`flex gap-2 whitespace-pre px-3 py-0.5 ${bg}`}
                      >
                        <span className="w-3 shrink-0 select-none">{prefix}</span>
                        <span className="min-w-0 break-words">{l.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2">
        {onReject && (
          <button
            type="button"
            onClick={onReject}
            className="rounded-md px-3 py-1.5 text-xs text-apolo-textTertiary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          onClick={handleApply}
          disabled={acceptedIds.size === 0}
          className="rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-apolo-accentHover disabled:opacity-50"
        >
          Aplicar {acceptedIds.size} / {hunks.length}
        </button>
      </footer>
    </div>
  );
}
